import { Alert, Box, Button, Grid, Paper, Stack, Typography } from '@mui/material'
import { DescriptionOutlined, LanguageOutlined, OpenInNewOutlined, PlayArrowOutlined, VisibilityOutlined } from '@mui/icons-material'
import { FunctionComponent } from 'react'
import moment from 'moment'
import { FormTextInputDisplay } from '../../form-field-components/new-and-edit/form-text-input'
import { FormDateInputDisplay } from '../../form-field-components/new-and-edit/form-date-input'
import { ISrsFigures, ISrsReport } from '../../../../shared/services/srs-report/srs-report.model'

const DOWNLOADED_FILES_PATH = '/settings/company-settings/downloaded-files'

const ARTEFACTS = [
  { key: 'srsPdf', label: 'Climate disclosure (PDF)', description: 'The four-pillar UK SRS S2 document.', icon: <DescriptionOutlined /> },
  { key: 'srsHtml', label: 'Web page (HTML)', description: 'A self-contained page you can publish on your own website.', icon: <LanguageOutlined /> },
]

/**
 * Final step: sign-off and generation. Generating freezes the figures into a snapshot and queues
 * both artefacts through the standard download queue; narratives stay editable afterwards but the
 * period and the published numbers do not change until the report is regenerated.
 */
export const SrsStepGenerate: FunctionComponent<{
  report: ISrsReport
  figures: ISrsFigures
  values: Record<string, any>
  isGenerating: boolean
  onChange: (field: string, value: any) => void
  onGenerate: () => void
  onViewReport: () => void
}> = ({ report, figures, values, isGenerating, onChange, onGenerate, onViewReport }) => {
  const isGenerated = report.status === 'GENERATED'
  const missingSignOff = !values.signOffName

  return (
    <>
      <Typography variant='h6' sx={{ mb: 1 }}>Sign-off and generate</Typography>
      <Typography variant='body2' sx={{ color: 'text.secondary', mb: 3 }}>
        Record who approved these disclosures, then generate the report. Generating freezes the
        figures shown in the previous step and produces the two files below; the figures will not
        change afterwards, even if the underlying emissions are edited. Narratives remain editable,
        and you can regenerate at any time to pick up newer data.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <FormTextInputDisplay
            isEditPage={false}
            disabled={false}
            title='Approved on behalf of the organisation by'
            name='signOffName'
            value={values.signOffName || ''}
            onChange={(event: any) => onChange('signOffName', event.target.value)}
            forceOutlined
            regularFieldLabel
            isTopRow
            isBottomRow
            helperText='The name of the director or board member approving the disclosures.'
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormTextInputDisplay
            isEditPage={false}
            disabled={false}
            title='Job title'
            name='signOffTitle'
            value={values.signOffTitle || ''}
            onChange={(event: any) => onChange('signOffTitle', event.target.value)}
            forceOutlined
            regularFieldLabel
            isTopRow
            isBottomRow
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <FormDateInputDisplay
            isEditPage={false}
            disabled={false}
            title='Sign-off date'
            name='signOffDate'
            value={values.signOffDate ? moment(values.signOffDate) : null}
            onChange={(value: any) => onChange('signOffDate', value ? moment(value).format('YYYY-MM-DD') : null)}
            isTopRow
            isBottomRow
          />
        </Grid>
      </Grid>

      {missingSignOff && (
        <Alert severity='warning' sx={{ mb: 3 }}>
          No sign-off name has been entered. You can still generate, but the approval section of the
          document will be blank.
        </Alert>
      )}

      {/* Each grid item is the flex container and its Paper stretches into it; height percentages on
          the Paper itself cannot resolve while the row's intrinsic height is being computed. */}
      <Box>
        <Grid container spacing={2}>
          {ARTEFACTS.map((artefact) => (
            <Grid item xs={12} md={6} key={artefact.key} sx={{ display: 'flex' }}>
              <Paper variant='outlined' sx={{ p: 2.5, flexGrow: 1 }}>
                <Stack direction='row' spacing={1.5} alignItems='flex-start'>
                  <Box sx={{ color: 'text.secondary' }}>{artefact.icon}</Box>
                  <Box>
                    <Typography variant='subtitle2'>{artefact.label}</Typography>
                    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                      {artefact.description}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button
          variant='contained'
          startIcon={<PlayArrowOutlined sx={{ fontSize: 'small' }} />}
          onClick={onGenerate}
          disabled={isGenerating}
          sx={{ textTransform: 'none' }}
        >
          <Typography sx={{ pl: 0.5 }}>
            {isGenerating
              ? 'Generating…'
              : (isGenerated ? 'Regenerate and freeze figures' : 'Generate and freeze figures')}
          </Typography>
        </Button>
      </Box>

      {isGenerated && (
        <Box sx={{ mt: 2 }}>
          {report.generatedAt && (
            <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }}>
              Last generated {moment(report.generatedAt).format('DD/MM/YYYY [at] HH:mm')}. Your files
              are in Settings, under Downloaded Files. View the report to read it as published and to
              manage its public link.
            </Typography>
          )}
          <Stack direction='row' flexWrap='wrap' useFlexGap spacing={1}>
            <Button
              variant='outlined'
              startIcon={<VisibilityOutlined sx={{ fontSize: 'small' }} />}
              onClick={onViewReport}
              sx={{ textTransform: 'none' }}
            >
              <Typography sx={{ pl: 0.5 }}>View report</Typography>
            </Button>
            {/* Opens in a new tab so a part-completed report is not lost by navigating away. */}
            <Button
              variant='outlined'
              component='a'
              href={DOWNLOADED_FILES_PATH}
              target='_blank'
              rel='noopener noreferrer'
              startIcon={<OpenInNewOutlined sx={{ fontSize: 'small' }} />}
              sx={{ textTransform: 'none' }}
            >
              <Typography sx={{ pl: 0.5 }}>View downloaded files</Typography>
            </Button>
          </Stack>
        </Box>
      )}

      {figures.preparedWithText && (
        <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary', mt: 4 }}>
          {figures.preparedWithText}
        </Typography>
      )}
    </>
  )
}
